import React from 'react';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import { densityTokens } from '../../theme/tokens';

/**
 * DataTable — server-driven table with all four states wired.
 *
 * SERVER-DRIVEN (docs/UI_STANDARDS.md §2.5): this component does not sort, filter,
 * or paginate in the browser. It renders exactly the rows it is given and reports
 * intent upward via onPageChange/onRowsPerPageChange. `totalCount` is the SERVER's
 * count, not rows.length.
 *
 * The state precedence is error > loading > empty > rows, and each renders real
 * content — unlike TablePageLayout, whose loading branch rendered an empty <Box/>,
 * and StandardTable, whose `loading` prop was never read (UI_STANDARDS §1.6).
 *
 * Props
 * - columns: [{ id, label, align, width, render?(row), field? }]
 * - rows: the current page of rows, exactly as the server returned them
 * - loading / error / onRetry
 * - emptyTitle / emptyMessage / emptyAction
 * - page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange (omit to hide pagination)
 * - density: 'compact' | 'standard' | 'comfortable'
 */
const DataTable = ({
    columns = [],
    rows = [],
    loading = false,
    error = null,
    onRetry,
    emptyTitle = 'No results',
    emptyMessage,
    emptyAction,
    getRowKey,
    onRowClick,
    page = 0,
    rowsPerPage = 10,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [10, 25, 50],
    density = 'standard',
    stickyHeader = false,
    maxHeight,
    sx = {},
}) => {
    const tokens = densityTokens(density);
    const paginated = typeof onPageChange === 'function' && typeof totalCount === 'number';

    const cellSx = { py: tokens.cellPaddingY, px: tokens.cellPaddingX };

    const body = () => {
        if (error) {
            return <ErrorState message={typeof error === 'string' ? error : undefined} onRetry={onRetry} />;
        }
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (!rows.length) {
            return <EmptyState title={emptyTitle} message={emptyMessage} action={emptyAction} />;
        }

        return (
            <TableContainer sx={{ maxHeight }}>
                <Table stickyHeader={stickyHeader} size={density === 'compact' ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align || 'left'}
                                    sx={{ ...cellSx, width: column.width, fontWeight: 700, whiteSpace: 'nowrap' }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow
                                key={getRowKey ? getRowKey(row, rowIndex) : row.id ?? rowIndex}
                                hover={Boolean(onRowClick)}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                sx={{
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    height: tokens.rowHeight,
                                }}
                            >
                                {columns.map((column) => (
                                    <TableCell key={column.id} align={column.align || 'left'} sx={cellSx}>
                                        {column.render ? column.render(row) : row[column.field || column.id]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', ...sx }}
        >
            {body()}
            {/* Gate on totalCount, not rows.length: an out-of-range page returns zero
                rows for a non-empty result set, and hiding pagination there would
                strand the user on an empty page with no way back. */}
            {paginated && !loading && !error && totalCount > 0 && (
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(_, nextPage) => onPageChange(nextPage)}
                    onRowsPerPageChange={
                        onRowsPerPageChange
                            ? (e) => onRowsPerPageChange(parseInt(e.target.value, 10))
                            : undefined
                    }
                    rowsPerPageOptions={onRowsPerPageChange ? rowsPerPageOptions : []}
                />
            )}
        </Paper>
    );
};

export default DataTable;
